import { useMemo, useState } from "react";
import { Mail, Plus, Download } from "lucide-react";
import PageHeader from "../../components/ui/PageHeader";
import ConfirmDialogue from "../../components/ConfirmDialogue";
import Pagination from "../../components/Pagination";
import { useAlert } from "../../../hooks/useAlert";

import CampaignStats from "./campaigns/campaign-stats";
import CampaignToolbar from "./campaigns/campaign-toolbar";
import CampaignTable from "./campaigns/campaign-table";
import CampaignDetailDrawer from "./campaigns/campaign-detail-drawer";
import CampaignWizard from "./campaigns/campaign-wizard";
import { CAMPAIGNS } from "./campaigns/campaign.data";

const PAGE_SIZE = 6;

export default function Campaigns() {
  const { showAlert } = useAlert();
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return CAMPAIGNS.filter((c) => {
      const matchTab = tab === "all" || c.status === tab;
      const matchSearch = !q || c.name.toLowerCase().includes(q) || c.subject.toLowerCase().includes(q);
      return matchTab && matchSearch;
    });
  }, [tab, search]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const safePage = Math.min(page, totalPages || 1);
  const rows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const handleTab = (t) => { setTab(t); setPage(1); };
  const handleSearch = (v) => { setSearch(v); setPage(1); };

  const confirmDelete = () => {
    showAlert({ type: "success", message: `Campaign "${toDelete.name}" deleted.` });
    setToDelete(null);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Campaigns"
        subtitle="Create, send and track your email campaigns."
        icon={Mail}
        actions={
          <>
            <button className="inline-flex items-center gap-2 text-sm font-medium px-3.5 py-2 rounded-xl border border-border bg-surface text-text_secondary hover:bg-surface_alt transition-colors">
              <Download className="w-3.5 h-3.5" /> Export
            </button>
            <button
              onClick={() => setWizardOpen(true)}
              className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-xl text-white"
              style={{ background: "linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)", boxShadow: "0 4px 16px rgba(53,53,93,0.22)" }}
            >
              <Plus className="w-4 h-4" /> New Campaign
            </button>
          </>
        }
      />

      <CampaignStats />

      <CampaignToolbar activeTab={tab} onTabChange={handleTab} search={search} onSearchChange={handleSearch} />

      <CampaignTable rows={rows} onView={setSelected} onDelete={setToDelete} />

      <Pagination page={safePage} totalPages={totalPages} onPageChange={setPage} />

      <CampaignDetailDrawer campaign={selected} open={!!selected} onClose={() => setSelected(null)} />

      <CampaignWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />

      <ConfirmDialogue
        open={!!toDelete}
        title="Delete campaign?"
        message={toDelete ? `"${toDelete.name}" will be permanently removed. This cannot be undone.` : ""}
        confirmLabel="Delete"
        danger
        onConfirm={confirmDelete}
        onCancel={() => setToDelete(null)}
      />
    </div>
  );
}
